import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, Copy, X, Loader2, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import DateField from "@/components/DateField";

/**
 * Interactive PPF + Windshield Protection Film builder.
 * Films: Veltik Clear PPF PRO 60 (body, 7.5 mil) / Veltik SEC-H WPF (glass, 6.5 mil).
 * All prices CAD before GST, server-authoritative (re-validated on /ppf/quote).
 * PPF never joins the Grand Opening promo or bundles. Full Vehicle and
 * oversized-glass windshield are custom-quote only.
 */

const money0 = (n) => `$${Math.round(Number(n)).toLocaleString("en-CA")}`;
const money2 = (n) => `$${Number(n).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Part prices: large painted panels scale by vehicle class from the D1
// baseline (multipliers come from the server config — single source of truth).
// Halves round up, identical to the backend round5.
const round5 = (n) => Math.round(n / 5) * 5;

const partPrice = (p, side = "both", count = 4, vc = null, mults = null) => {
  const base = p.kind === "flat" ? p.price : p.kind === "sided" ? (side === "both" ? p.pair : p.single)
    : (count >= 4 ? p.all4 : (count === 2 && p.pair != null ? p.pair : p.each * count));
  const mult = vc && p.class_scaled && mults ? (mults[vc] || 1) : 1;
  return mult !== 1 ? round5(base * mult) : base;
};

// Package price: exact class price once the vehicle is classified, otherwise
// "From $X" with X = the lowest class price (D1). Class names stay internal.
const pkgPrice = (p, vc) =>
  p.prices ? (vc ? (p.prices[vc] ?? null) : Math.min(...Object.values(p.prices)))
    : (p.quote_only ? p.from_price : p.price);

const priceText = (p, vc = null, mults = null) =>
  p.kind === "flat" ? money0(partPrice(p, "both", 4, vc, mults))
    : p.kind === "sided" ? `${money0(partPrice(p, "driver", 1, vc, mults))} / ${money0(partPrice(p, "both", 4, vc, mults))} pair`
    : p.pair != null ? `${money0(p.each)} ea / ${money0(p.pair)} pair / ${money0(p.all4)} all 4`
    : `${money0(p.each)} ea / ${money0(p.all4)} all 4`;

const SIDE_LABEL = { driver: "Driver", passenger: "Passenger", both: "Both" };

// Popular one-tap add-ons shown right under the packages (mobile-first flow);
// every other panel stays available under "Show all individual panels".
const QUICK_PARTS = ["rocker-panels", "headlights", "door-cups", "trunk-ledge"];

const hasAnySelection = (sel) => Boolean(sel && (sel.package || sel.windshield || (sel.extra_parts || []).length));

export default function PpfBuilder({ year, make, model, vehicleClass, onBook, onChange, initial, initialNonce = 0, testid = "ppf" }) {
  const [cfg, setCfg] = useState(null);
  const [pkgKey, setPkgKey] = useState(initial?.package || null);
  const [removed, setRemoved] = useState(initial?.removed_parts || []);  // part keys removed from the package
  const [extras, setExtras] = useState(() =>                            // key -> {side, count}
    Object.fromEntries((initial?.extra_parts || []).map((s) => [s.key, { side: s.side || "both", count: s.count || 4 }])));
  const [windshield, setWindshield] = useState(Boolean(initial?.windshield));
  const [showAllPanels, setShowAllPanels] = useState(false);
  const [serverQuote, setServerQuote] = useState(null);
  const [copied, setCopied] = useState(false);
  // Custom-quote form (Full Vehicle / oversized glass)
  const [qForm, setQForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [qSent, setQSent] = useState(false);
  const [qBusy, setQBusy] = useState(false);
  const [qErr, setQErr] = useState("");
  // Free assessment visit (Full Vehicle quotes): a real 30-min slot via the
  // regular booking engine (service key ppf-assessment).
  const [assessOpen, setAssessOpen] = useState(false);
  const [assessDate, setAssessDate] = useState("");
  const [assessSlots, setAssessSlots] = useState(null);
  const [assessTime, setAssessTime] = useState("");
  const [assessBusy, setAssessBusy] = useState(false);
  const [assessDone, setAssessDone] = useState(null);
  const [bookingCfg, setBookingCfg] = useState(null);

  useEffect(() => {
    if (!assessOpen || bookingCfg) return;
    api.get("/pricing/config").then(({ data }) => setBookingCfg(data.booking || {})).catch(() => {});
  }, [assessOpen, bookingCfg]);

  useEffect(() => {
    if (!assessDate) { setAssessSlots(null); return; }
    let cancelled = false;
    setAssessTime("");
    setAssessSlots(null);
    api.get("/booking/slots", { params: { date: assessDate, service_keys: "ppf-assessment" } })
      .then(({ data }) => { if (!cancelled) setAssessSlots(data); })
      .catch(() => { if (!cancelled) setAssessSlots(null); });
    return () => { cancelled = true; };
  }, [assessDate]);

  const submitAssessment = async () => {
    setQErr("");
    if (!vehicleReady) { setQErr("Pick your vehicle above first"); return; }
    if (!qForm.name.trim()) { setQErr("Please enter your name"); return; }
    if (qForm.phone.trim().length < 4) { setQErr("Please enter your phone"); return; }
    if (!/.+@.+\..+/.test(qForm.email.trim())) { setQErr("Please enter a valid email"); return; }
    if (!assessDate || !assessTime) { setQErr("Pick a date and time for the visit"); return; }
    setAssessBusy(true);
    try {
      const { data } = await api.post("/booking/book", {
        name: qForm.name.trim(),
        phone: qForm.phone.trim(),
        email: qForm.email.trim(),
        message: `Full Vehicle PPF — assessment visit.${qForm.message.trim() ? " " + qForm.message.trim() : ""}`,
        year: Number(year), make, model,
        service_keys: ["ppf-assessment"],
        date: assessDate, time: assessTime,
      });
      setAssessDone(data);
    } catch (e) {
      setQErr(e?.response?.data?.detail || "Could not book the visit — please call us.");
    } finally {
      setAssessBusy(false);
    }
  };

  useEffect(() => { api.get("/ppf/config").then(({ data }) => setCfg(data)).catch(() => {}); }, []);

  // Late preselection (Popular-option cards): re-seed the builder when a new
  // payload arrives after mount. First mount already seeds via useState above.
  const lastNonce = useRef(initialNonce);
  useEffect(() => {
    if (initialNonce === lastNonce.current) return;
    lastNonce.current = initialNonce;
    if (!initial) return;
    setPkgKey(initial.package || null);
    setRemoved(initial.removed_parts || []);
    setExtras(Object.fromEntries((initial.extra_parts || []).map((s) => [s.key, { side: s.side || "both", count: s.count || 4 }])));
    setWindshield(Boolean(initial.windshield));
  }, [initialNonce, initial]);

  const vehicleReady = Boolean(year && make && model);
  const pkg = cfg?.packages.find((p) => p.key === pkgKey) || null;
  const partMap = useMemo(() => Object.fromEntries((cfg?.parts || []).map((p) => [p.key, p])), [cfg]);
  const mults = cfg?.part_class_multipliers || null;
  const fvCovers = useMemo(() => new Set(cfg?.full_vehicle_covers || []), [cfg]);
  const keptParts = pkg && !pkg.quote_only ? pkg.parts.filter((p) => !removed.includes(p.key)) : [];
  const keptKeys = useMemo(() => new Set(keptParts.map((p) => p.key)), [keptParts]);
  // A part is unavailable as an extra when the selected package already covers
  // it — explicitly (kept part) or implicitly (Full Vehicle covers everything painted).
  const coveredBy = (key) => keptKeys.has(key) || (Boolean(pkg?.quote_only) && fvCovers.has(key));

  // ---- local estimate (instant feedback; server re-validates when vehicle known)
  const local = useMemo(() => {
    if (!cfg) return null;
    const lines = [];
    if (pkg) {
      if (pkg.quote_only) {
        lines.push({ label: `${pkg.name} PPF`, amount: null, preliminary: true,
                     vehicle_price: vehicleClass && pkg.prices ? pkg.prices[vehicleClass] : null });
      } else {
        const clsPrice = pkgPrice(pkg, vehicleClass);  // null until the vehicle is classified
        const origSum = pkg.parts.reduce((s, p) => s + partPrice(partMap[p.key], p.side || "both", p.count || 4, vehicleClass, mults), 0);
        const keepSum = keptParts.reduce((s, p) => s + partPrice(partMap[p.key], p.side || "both", p.count || 4, vehicleClass, mults), 0);
        const amount = clsPrice == null ? null
          : removed.length === 0 ? clsPrice : (origSum ? Math.round(clsPrice * keepSum / origSum) : 0);
        lines.push({ label: `${pkg.name} PPF package`, amount });
      }
    }
    for (const [key, sel] of Object.entries(extras)) {
      const p = partMap[key];
      if (!p) continue;
      if (pkg?.quote_only && fvCovers.has(key)) continue;  // already covered by Full Vehicle
      // Upgrade: package already covers the partial version → difference only
      if (p.upgrade_of && keptKeys.has(p.upgrade_of)) {
        const diff = partPrice(p, sel.side, sel.count, vehicleClass, mults)
                   - partPrice(partMap[p.upgrade_of], sel.side, sel.count, vehicleClass, mults);
        lines.push({ label: `Upgrade: ${partMap[p.upgrade_of].label} → ${p.label}`, amount: Math.max(0, diff) });
        continue;
      }
      const lbl = p.kind === "sided" && sel.side !== "both" ? `${p.label} (${SIDE_LABEL[sel.side]} side)`
        : p.kind === "count4" ? `${p.label} (${sel.count >= 4 ? "all 4" : sel.count})` : p.label;
      lines.push({ label: lbl, amount: partPrice(p, sel.side, sel.count, vehicleClass, mults) });
    }
    if (windshield) {
      const wp = vehicleClass ? cfg.windshield.prices[vehicleClass] : null;
      lines.push({ label: cfg.windshield.name, amount: wp ?? null });
    }
    const subtotal = lines.reduce((s, l) => s + (l.amount || 0), 0);
    const gst = Math.round(subtotal * cfg.gst_rate * 100) / 100;
    return { lines, subtotal, gst, total: Math.round((subtotal + gst) * 100) / 100 };
  }, [cfg, pkg, keptParts, keptKeys, extras, windshield, vehicleClass, removed.length, partMap, mults, fvCovers]);

  // ---- server-authoritative quote once the vehicle is known
  const selectionPayload = useMemo(() => ({
    package: pkgKey,
    removed_parts: removed,
    extra_parts: Object.entries(extras).map(([key, s]) => ({ key, side: s.side, count: s.count })),
    windshield,
  }), [pkgKey, removed, extras, windshield]);

  useEffect(() => {
    setServerQuote(null);
    if (!vehicleReady || !local || local.lines.length === 0) return;
    let cancelled = false;
    const t = setTimeout(() => {
      api.post("/ppf/quote", { year: Number(year), make, model, ...selectionPayload })
        .then(({ data }) => { if (!cancelled) setServerQuote(data); })
        .catch(() => { if (!cancelled) setServerQuote(null); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleReady, year, make, model, selectionPayload]);

  const quote = serverQuote || local;
  const quoteRequired = Boolean(
    (pkg && pkg.quote_only) ||
    (windshield && vehicleClass && cfg && cfg.windshield.prices[vehicleClass] == null));

  // Let the parent wizard (QuickBook) follow the selection + totals
  const quoteSig = quote ? `${quote.subtotal}|${quote.total}|${quote.lines.length}` : "";
  useEffect(() => {
    if (onChange) onChange(selectionPayload, hasAnySelection(selectionPayload) ? quote : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionPayload, quoteSig, serverQuote]);

  const selectPackage = (key) => {
    if (pkgKey === key) { setPkgKey(null); setRemoved([]); return; }
    setPkgKey(key);
    setRemoved([]);
    // drop extras that are part of the new package (no double charging)
    const pkgDef = cfg.packages.find((p) => p.key === key);
    if (pkgDef && !pkgDef.quote_only) {
      const included = new Set(pkgDef.parts.map((p) => p.key));
      setExtras((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => !included.has(k))));
    }
    if (pkgDef && pkgDef.quote_only) {
      // Full Vehicle already covers every painted panel — drop those extras
      setExtras((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => !fvCovers.has(k))));
    }
  };

  const toggleExtra = (p) => {
    setExtras((prev) => {
      const next = { ...prev };
      if (next[p.key]) { delete next[p.key]; return next; }
      // conflicts: partial vs full hood / fenders — replace, never stack
      for (const c of p.conflicts || []) delete next[c];
      next[p.key] = { side: "both", count: 4 };
      return next;
    });
  };

  const setExtraOpt = (key, patch) =>
    setExtras((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  // Extra-part amount: when the package already includes the partial version,
  // the full version costs only the class-priced difference (upgrade).
  const extraAmount = (p, side = "both", count = 4) => {
    const full = partPrice(p, side, count, vehicleClass, mults);
    if (p.upgrade_of && keptKeys.has(p.upgrade_of)) {
      return { amount: Math.max(0, full - partPrice(partMap[p.upgrade_of], side, count, vehicleClass, mults)), upgrade: true };
    }
    return { amount: full, upgrade: false };
  };

  const estimateText = () => {
    if (!quote) return "";
    const lines = [
      "WTD Detailing & Tint — PPF Estimate",
      vehicleReady ? `Vehicle: ${year} ${make} ${model}` : null,
      ...quote.lines.map((l) => `${l.label}: ${l.amount == null
        ? (l.preliminary && l.vehicle_price != null
            ? `${money0(l.vehicle_price)} + GST (preliminary — confirmed after a free inspection)`
            : "custom quote")
        : money0(l.amount)}`),
      `Subtotal (before GST): ${money2(quote.subtotal)}`,
      `GST (5%): ${money2(quote.gst)}`,
      quoteRequired ? "Total: custom quote — confirmed after vehicle assessment" : `Estimated total: ${money2(quote.total)}`,
      "Body film: Veltik Clear PPF PRO 60 (7.5 mil) · Windshield: Veltik SEC-H WPF (6.5 mil)",
      "Prices in CAD. Final price confirmed at drop-off.",
    ].filter(Boolean);
    return lines.join("\n");
  };

  const copyEstimate = async () => {
    try { await navigator.clipboard.writeText(estimateText()); } catch {
      const ta = document.createElement("textarea");
      ta.value = estimateText();
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* clipboard unavailable */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const submitQuoteRequest = async () => {
    setQErr("");
    if (!qForm.name.trim()) { setQErr("Please enter your name"); return; }
    if (qForm.phone.trim().length < 4) { setQErr("Please enter your phone"); return; }
    if (!/.+@.+\..+/.test(qForm.email.trim())) { setQErr("Please enter a valid email"); return; }
    setQBusy(true);
    try {
      const what = [
        pkg?.quote_only ? "Full Vehicle PPF" : null,
        windshield ? "Windshield Protection Film" : null,
      ].filter(Boolean).join(" + ");
      await api.post("/quotes", {
        name: qForm.name.trim(),
        phone: qForm.phone.trim(),
        email: qForm.email.trim(),
        vehicle: vehicleReady ? `${year} ${make} ${model}` : "To be confirmed",
        service: `${what} — custom quote request`,
        message: [qForm.message.trim(), quote ? `\n--- Estimate context ---\n${estimateText()}` : ""].filter(Boolean).join(""),
        estimatedPrice: null,
        estimatePayload: { ppf: selectionPayload, vehicle_class: vehicleClass || null },
      });
      setQSent(true);
    } catch (e) {
      setQErr(e?.response?.data?.detail || "Could not send the request — please call us.");
    } finally {
      setQBusy(false);
    }
  };

  if (!cfg) return <div className="text-white/40 text-sm py-8" data-testid={`${testid}-loading`}>Loading PPF catalog…</div>;

  const hasSelection = Boolean(pkgKey || Object.keys(extras).length || windshield);
  const groups = [...new Set(cfg.parts.map((p) => p.group))];

  return (
    <div data-testid={`${testid}-builder`} className="space-y-6">
      {/* Packages */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {cfg.packages.map((p) => {
          const active = pkgKey === p.key;
          return (
            <button key={p.key} type="button" onClick={() => selectPackage(p.key)}
              data-testid={`${testid}-pkg-${p.key}`}
              className={`text-left p-4 rounded-xl border transition-colors ${
                active ? "bg-[#1F5EFF]/15 border-[#1F5EFF]" : "border-white/10 bg-black/30 hover:border-[#1F5EFF]/50"}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-mono-alt text-[9px] uppercase tracking-widest text-[#8db2ff]">{p.tag}</div>
                  <div className="text-white font-medium mt-0.5">{p.name}</div>
                </div>
                <div className="text-right shrink-0">
                  {p.quote_only ? (
                    vehicleClass && pkgPrice(p, vehicleClass) != null ? (
                      <>
                        <div className="text-white font-medium tabular-nums" data-testid={`${testid}-pkg-price-${p.key}`}>{money0(pkgPrice(p, vehicleClass))} + GST</div>
                        <div className="text-[9px] font-mono-alt uppercase tracking-widest text-amber-300/80">After free inspection</div>
                      </>
                    ) : (
                      <>
                        <div className="text-white font-medium tabular-nums" data-testid={`${testid}-pkg-price-${p.key}`}>From {money0(pkgPrice(p, null))}</div>
                        <div className="text-[9px] font-mono-alt uppercase tracking-widest text-amber-300/80">After free inspection</div>
                      </>
                    )
                  ) : vehicleClass ? (
                    <div className="text-white font-medium tabular-nums" data-testid={`${testid}-pkg-price-${p.key}`}>{money0(pkgPrice(p, vehicleClass))}</div>
                  ) : (
                    <div className="text-white font-medium tabular-nums" data-testid={`${testid}-pkg-price-${p.key}`}>From {money0(pkgPrice(p, null))}</div>
                  )}
                </div>
              </div>
              <div className="text-[11px] text-white/45 mt-1 leading-snug">{p.blurb}</div>
              {active && <div className="mt-2 text-[10px] font-mono-alt uppercase tracking-widest text-[#8db2ff]">✓ Selected</div>}
            </button>
          );
        })}
      </div>

      {/* Package contents — removable with proportional re-pricing; partial
          coverage offers its full version as a priced upgrade */}
      {pkg && !pkg.quote_only && (
        <div className="rounded-xl border border-white/10 bg-black/20 p-4" data-testid={`${testid}-pkg-contents`}>
          <div className="font-mono-alt text-[9px] uppercase tracking-widest text-white/40 mb-2">
            Included in {pkg.name} — remove what you don't need (price adjusts proportionally)
          </div>
          <div className="flex flex-wrap gap-1.5">
            {pkg.parts.map((pp) => {
              const part = partMap[pp.key];
              const isRemoved = removed.includes(pp.key);
              return (
                <button key={pp.key} type="button"
                  onClick={() => setRemoved((r) => isRemoved ? r.filter((k) => k !== pp.key) : [...r, pp.key])}
                  data-testid={`${testid}-pkg-part-${pp.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] transition-colors ${
                    isRemoved ? "border-white/10 text-white/30 line-through" : "border-[#1F5EFF]/40 bg-[#1F5EFF]/10 text-white/85"}`}>
                  {isRemoved ? <X className="w-3 h-3" /> : <Check className="w-3 h-3 text-[#8db2ff]" />}
                  {part?.label}
                </button>
              );
            })}
          </div>
          {/* Upgrade chips: partial coverage → full version at the difference */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {cfg.parts.filter((p) => p.upgrade_of && keptKeys.has(p.upgrade_of)).map((p) => {
              const diff = partPrice(p, "both", 4, vehicleClass, mults)
                         - partPrice(partMap[p.upgrade_of], "both", 4, vehicleClass, mults);
              const sel = extras[p.key];
              return (
                <button key={p.key} type="button" onClick={() => toggleExtra(p)}
                  data-testid={`${testid}-upgrade-${p.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] transition-colors ${
                    sel ? "border-emerald-400/50 bg-emerald-500/15 text-emerald-200"
                        : "border-dashed border-amber-400/40 text-amber-200/80 hover:border-amber-300/70"}`}>
                  {sel ? `✓ ${p.label} — upgrade added` : `↑ Upgrade to ${p.label} +${money0(diff)}`}
                </button>
              );
            })}
          </div>
          {removed.length > 0 && (
            <div className="mt-2 text-[10px] text-amber-300/70" data-testid={`${testid}-pkg-adjusted-note`}>
              Package price re-calculated proportionally for the remaining coverage.
            </div>
          )}
        </div>
      )}

      {/* Popular add-ons — one tap, right after the packages */}
      <div data-testid={`${testid}-quick-addons`}>
        <div className="font-mono-alt text-[9px] uppercase tracking-widest text-white/40 mb-2">
          Popular add-ons — tap to add
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
          <div className={`rounded-lg border px-3 py-2 ${windshield ? "border-emerald-400/50 bg-emerald-500/8" : "border-white/10 bg-black/20"}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[12px] leading-tight text-white/85">Windshield Protection</div>
                <div className="text-[10px] text-white/35 tabular-nums" data-testid={`${testid}-windshield-price`}>
                  {vehicleClass
                    ? (cfg.windshield.prices[vehicleClass] != null ? money0(cfg.windshield.prices[vehicleClass]) : "By quote")
                    : (() => { const vs = Object.values(cfg.windshield.prices).filter((v2) => v2 != null); return `${money0(Math.min(...vs))}–${money0(Math.max(...vs))}`; })()}
                </div>
              </div>
              <button type="button" onClick={() => setWindshield((w2) => !w2)}
                data-testid={`${testid}-windshield-toggle`}
                className={`shrink-0 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-colors ${
                  windshield ? "bg-emerald-500/20 border border-emerald-400/50 text-emerald-300"
                    : "border border-[#1F5EFF]/40 text-[#8db2ff] hover:bg-[#1F5EFF]/15"}`}>
                {windshield ? "✓ Added" : "Add"}
              </button>
            </div>
          </div>
          {QUICK_PARTS.map((key) => {
            const p = partMap[key];
            if (!p) return null;
            const inPackage = coveredBy(key);
            const sel = extras[key];
            return (
              <div key={key} className={`rounded-lg border px-3 py-2 ${sel ? "border-emerald-400/50 bg-emerald-500/8" : "border-white/10 bg-black/20"}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className={`text-[12px] leading-tight ${inPackage ? "text-white/35" : "text-white/85"}`}>{p.label}</div>
                    <div className="text-[10px] text-white/35 tabular-nums">{priceText(p, vehicleClass, mults)}</div>
                  </div>
                  {inPackage ? (
                    <span className="shrink-0 text-[9px] font-mono-alt uppercase tracking-widest text-[#8db2ff]">In package</span>
                  ) : (
                    <button type="button" onClick={() => toggleExtra(p)}
                      data-testid={`${testid}-quick-${key}`}
                      className={`shrink-0 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-colors ${
                        sel ? "bg-emerald-500/20 border border-emerald-400/50 text-emerald-300"
                          : "border border-[#1F5EFF]/40 text-[#8db2ff] hover:bg-[#1F5EFF]/15"}`}>
                      {sel ? "✓ Added" : "Add"}
                    </button>
                  )}
                </div>
                {sel && p.kind === "sided" && (
                  <div className="mt-1.5 flex gap-1" data-testid={`${testid}-side-${key}`}>
                    {["driver", "passenger", "both"].map((s) => (
                      <button key={s} type="button" onClick={() => setExtraOpt(key, { side: s })}
                        data-testid={`${testid}-side-${key}-${s}`}
                        className={`px-2 py-0.5 rounded text-[10px] transition-colors ${
                          sel.side === s ? "bg-emerald-500/25 text-emerald-200" : "text-white/40 hover:text-white/70"}`}>
                        {SIDE_LABEL[s]} · {money0(extraAmount(p, s).amount)}
                      </button>
                    ))}
                  </div>
                )}
                {sel && p.kind === "count4" && (
                  <div className="mt-1.5 flex gap-1" data-testid={`${testid}-count-${key}`}>
                    {(p.pair != null ? [1, 2, 4] : [1, 2, 3, 4]).map((n) => (
                      <button key={n} type="button" onClick={() => setExtraOpt(key, { count: n })}
                        data-testid={`${testid}-count-${key}-${n}`}
                        className={`px-2 py-0.5 rounded text-[10px] transition-colors ${
                          sel.count === n ? "bg-emerald-500/25 text-emerald-200" : "text-white/40 hover:text-white/70"}`}>
                        {n === 4 ? "All 4" : n} · {money0(partPrice(p, "both", n, vehicleClass, mults))}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-1.5 text-[10px] text-white/30">
          {cfg.windshield.film} — 6.5 mil exterior glass protection film (not window tint). Hydrophobic, high optical clarity.
          {windshield && vehicleClass && cfg.windshield.prices[vehicleClass] == null && (
            <span className="text-amber-300/80"> Oversized / complex glass — priced individually.</span>
          )}
        </div>
      </div>

      {/* Individual panels — expandable, quick picks live above */}
      <div data-testid={`${testid}-parts`}>
        <button type="button" onClick={() => setShowAllPanels((s) => !s)}
          data-testid={`${testid}-show-all-panels`} aria-expanded={showAllPanels}
          className="flex items-center gap-2 font-mono-alt text-[9px] uppercase tracking-widest text-white/40 hover:text-white/70 transition-colors mb-2">
          {showAllPanels ? "Hide individual panels" : "Show all individual panels"}
          <ChevronDown className={`w-3 h-3 transition-transform ${showAllPanels ? "rotate-180" : ""}`} />
        </button>
        {showAllPanels && (
        <div className="space-y-3">
          {groups.filter((g) => cfg.parts.some((p) => p.group === g && !QUICK_PARTS.includes(p.key))).map((g) => (
            <div key={g}>
              <div className="text-[10px] text-white/30 uppercase tracking-widest mb-1">{g}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {cfg.parts.filter((p) => p.group === g && !QUICK_PARTS.includes(p.key)).map((p) => {
                  const inPackage = coveredBy(p.key);
                  const sel = extras[p.key];
                  const conflictsSelected = (p.conflicts || []).some((c) => extras[c]);
                  const ea = extraAmount(p);
                  return (
                    <div key={p.key} data-testid={`${testid}-part-${p.key}`}
                      className={`rounded-lg border px-3 py-2 ${sel ? "border-emerald-400/50 bg-emerald-500/8" : "border-white/10 bg-black/20"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className={`text-[12px] leading-tight ${inPackage ? "text-white/35" : "text-white/85"}`}>{p.label}</div>
                          <div className="text-[10px] text-white/35 tabular-nums">
                            {ea.upgrade ? `Upgrade from partial: +${money0(ea.amount)}` : priceText(p, vehicleClass, mults)}
                          </div>
                        </div>
                        {inPackage ? (
                          <span className="shrink-0 text-[9px] font-mono-alt uppercase tracking-widest text-[#8db2ff]">In package</span>
                        ) : (
                          <button type="button" onClick={() => toggleExtra(p)}
                            data-testid={`${testid}-part-toggle-${p.key}`}
                            className={`shrink-0 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-colors ${
                              sel ? "bg-emerald-500/20 border border-emerald-400/50 text-emerald-300"
                                : "border border-[#1F5EFF]/40 text-[#8db2ff] hover:bg-[#1F5EFF]/15"}`}>
                            {sel ? "✓ Added" : ea.upgrade ? `Upgrade +${money0(ea.amount)}` : conflictsSelected ? "Swap" : "Add"}
                          </button>
                        )}
                      </div>
                      {sel && p.kind === "sided" && (
                        <div className="mt-1.5 flex gap-1" data-testid={`${testid}-side-${p.key}`}>
                          {["driver", "passenger", "both"].map((s) => (
                            <button key={s} type="button" onClick={() => setExtraOpt(p.key, { side: s })}
                              data-testid={`${testid}-side-${p.key}-${s}`}
                              className={`px-2 py-0.5 rounded text-[10px] transition-colors ${
                                sel.side === s ? "bg-emerald-500/25 text-emerald-200" : "text-white/40 hover:text-white/70"}`}>
                              {SIDE_LABEL[s]} · {money0(extraAmount(p, s).amount)}
                            </button>
                          ))}
                        </div>
                      )}
                      {sel && p.kind === "count4" && (
                        <div className="mt-1.5 flex gap-1" data-testid={`${testid}-count-${p.key}`}>
                          {(p.pair != null ? [1, 2, 4] : [1, 2, 3, 4]).map((n) => (
                            <button key={n} type="button" onClick={() => setExtraOpt(p.key, { count: n })}
                              data-testid={`${testid}-count-${p.key}-${n}`}
                              className={`px-2 py-0.5 rounded text-[10px] transition-colors ${
                                sel.count === n ? "bg-emerald-500/25 text-emerald-200" : "text-white/40 hover:text-white/70"}`}>
                              {n === 4 ? "All 4" : n} · {money0(partPrice(p, "both", n, vehicleClass, mults))}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* Estimate */}
      {hasSelection && quote && (() => {
        // any line without a class price (e.g. windshield before the vehicle
        // is known) makes the total a custom quote — never show "$0.00"
        const hasUnpriced = quote.lines.some((l) => l.amount == null);
        const totalsKnown = !quoteRequired && !hasUnpriced;
        return (
        <div className="rounded-2xl border border-white/10 bg-black/30 p-5" data-testid={`${testid}-estimate`}>
          <div className="space-y-1.5">
            {quote.lines.map((l, i) => (
              <div key={i} className="flex items-baseline justify-between gap-3 text-[13px]">
                <span className="text-white/70">{l.label}</span>
                <span className="text-white tabular-nums shrink-0" data-testid={l.preliminary ? `${testid}-preliminary-price` : undefined}>
                  {l.amount == null
                    ? (l.preliminary && l.vehicle_price != null ? `${money0(l.vehicle_price)} + GST` : "custom quote")
                    : money0(l.amount)}
                </span>
              </div>
            ))}
          </div>
          {quote.lines.some((l) => l.preliminary) && (
            <div className="mt-2 text-[11px] text-amber-300/80 leading-snug" data-testid={`${testid}-preliminary-note`}>
              Preliminary estimate. Final pricing will be confirmed after a free vehicle inspection.
            </div>
          )}
          {totalsKnown ? (
          <div className="mt-3 pt-3 border-t border-white/10 space-y-1">
            <div className="flex justify-between text-[13px]">
              <span className="text-white/50">Subtotal (before GST)</span>
              <span className="text-white tabular-nums" data-testid={`${testid}-subtotal`}>{money2(quote.subtotal)}</span>
            </div>
            <div className="flex justify-between text-[13px]">
              <span className="text-white/50">GST (5%)</span>
              <span className="text-white tabular-nums" data-testid={`${testid}-gst`}>{money2(quote.gst)}</span>
            </div>
            <div className="flex justify-between text-base pt-1">
              <span className="text-white font-medium">Estimated total</span>
              <span className="text-white font-semibold tabular-nums" data-testid={`${testid}-total`}>
                {money2(quote.total)}
              </span>
            </div>
          </div>
          ) : (
          <div className="mt-3 pt-3 border-t border-white/10 text-[12px] text-amber-300/80" data-testid={`${testid}-total-pending`}>
            {quote.lines.some((l) => l.preliminary)
              ? "Full Vehicle PPF is booked through a free 30-minute inspection — the final price is confirmed there, no payment is taken now."
              : "Select your vehicle above for exact pricing — the total appears once everything is priced for your vehicle."}
          </div>
          )}
          <div className="mt-2 text-[10px] text-white/35 leading-snug">
            CAD. Body film: {cfg.film_body} (7.5 mil). PPF launch pricing — not combined with other promotions.
            Final price confirmed at drop-off.
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <button type="button" onClick={copyEstimate} data-testid={`${testid}-copy-estimate`}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-white/15 text-white/80 text-xs font-semibold hover:border-white/40 transition-colors">
              <Copy className="w-3.5 h-3.5" /> {copied ? "Copied ✓" : "Copy Estimate"}
            </button>
            {totalsKnown && onBook && (
              <button type="button"
                onClick={() => onBook(selectionPayload, quote)}
                data-testid={`${testid}-book-btn`}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#1F5EFF] text-white text-xs font-semibold hover:bg-[#8db2ff] hover:text-black transition-colors">
                Request this date →
              </button>
            )}
          </div>
        </div>
        );
      })()}

      {/* Custom quote form (Full Vehicle / oversized glass) */}
      {quoteRequired && hasSelection && (
        <div className="rounded-2xl border border-amber-400/30 bg-amber-400/5 p-5" data-testid={`${testid}-quote-form`}>
          {qSent ? (
            <div className="text-sm text-emerald-300" data-testid={`${testid}-quote-sent`}>
              Request received — we'll assess your vehicle and send the exact quote. No payment is taken for an unconfirmed amount.
            </div>
          ) : (
            <>
              <div className="text-white font-medium text-sm">Get your exact quote</div>
              <p className="text-[11px] text-white/50 mt-1 leading-snug">
                {pkg?.quote_only
                  ? "Full Vehicle PPF is priced after an in-person assessment of your vehicle's size and complexity."
                  : "Windshield film for this vehicle is priced individually (oversized / complex glass)."}
                {" "}Leave your contacts — we reply with a confirmed price. No payment now.
              </p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input value={qForm.name} onChange={(e) => setQForm({ ...qForm, name: e.target.value })}
                  placeholder="Name" data-testid={`${testid}-quote-name`}
                  className="px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-white text-sm outline-none focus:border-[#1F5EFF]" />
                <input value={qForm.phone} onChange={(e) => setQForm({ ...qForm, phone: e.target.value })}
                  placeholder="Phone" data-testid={`${testid}-quote-phone`}
                  className="px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-white text-sm outline-none focus:border-[#1F5EFF]" />
                <input value={qForm.email} onChange={(e) => setQForm({ ...qForm, email: e.target.value })}
                  placeholder="Email" type="email" data-testid={`${testid}-quote-email`}
                  className="px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-white text-sm outline-none focus:border-[#1F5EFF] sm:col-span-2" />
                <textarea value={qForm.message} onChange={(e) => setQForm({ ...qForm, message: e.target.value })}
                  placeholder="Anything we should know? (optional)" rows={2} data-testid={`${testid}-quote-message`}
                  className="px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-white text-sm outline-none focus:border-[#1F5EFF] sm:col-span-2" />
              </div>
              {qErr && <div className="mt-2 text-[11px] text-red-300" data-testid={`${testid}-quote-error`}>{qErr}</div>}
              <button type="button" onClick={submitQuoteRequest} disabled={qBusy}
                data-testid={`${testid}-quote-submit`}
                className="mt-3 w-full px-4 py-2.5 rounded-xl bg-[#1F5EFF] text-white text-xs font-semibold hover:bg-[#8db2ff] hover:text-black transition-colors disabled:opacity-50">
                {qBusy ? "Sending…" : "Request exact quote"}
              </button>

              {/* Free assessment visit — a real 30-min slot instead of the form */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <button type="button" onClick={() => setAssessOpen((o) => !o)}
                  data-testid={`${testid}-assess-toggle`}
                  className="text-[12px] text-[#8db2ff] hover:text-white transition-colors">
                  {assessOpen ? "— hide visit booking" : "…or book a free 30-min assessment visit →"}
                </button>
                {assessOpen && !assessDone && (
                  <div className="mt-3" data-testid={`${testid}-assess-form`}>
                    <DateField value={assessDate} onChange={setAssessDate}
                      min={new Date().toLocaleDateString("en-CA")}
                      max={new Date(Date.now() + (bookingCfg?.horizon_days || 30) * 864e5).toLocaleDateString("en-CA")}
                      testid={`${testid}-assess-date`} placeholder="Pick a date"
                      className="w-full px-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-white text-sm" />
                    {assessDate && (
                      assessSlots === null ? (
                        <div className="mt-2 text-white/40 text-xs flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking availability…</div>
                      ) : assessSlots.closed ? (
                        <div className="mt-2 text-amber-300/80 text-xs">We're closed on this day — pick another date.</div>
                      ) : (
                        <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-1.5" data-testid={`${testid}-assess-slots`}>
                          {assessSlots.slots.filter((s) => s.available).map((s) => (
                            <button key={s.time} type="button" onClick={() => setAssessTime(s.time)}
                              data-testid={`${testid}-assess-slot-${s.time}`}
                              className={`py-1.5 rounded-lg text-[11px] font-mono-alt border transition-colors ${
                                assessTime === s.time ? "bg-[#1F5EFF] border-[#1F5EFF] text-white" : "border-white/12 text-white/70 hover:border-[#1F5EFF]/60"}`}>
                              {s.time}
                            </button>
                          ))}
                        </div>
                      )
                    )}
                    <button type="button" onClick={submitAssessment} disabled={assessBusy || !assessTime}
                      data-testid={`${testid}-assess-submit`}
                      className="mt-3 w-full px-4 py-2.5 rounded-xl border border-emerald-400/50 bg-emerald-500/15 text-emerald-300 text-xs font-semibold hover:bg-emerald-500/25 transition-colors disabled:opacity-50">
                      {assessBusy ? "Booking…" : "Book the free visit"}
                    </button>
                  </div>
                )}
                {assessDone && (
                  <div className="mt-3 text-sm text-emerald-300" data-testid={`${testid}-assess-done`}>
                    Visit booked — {assessDone.date} at {assessDone.time}. We'll confirm shortly by email.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
